import * as uuid from "uuid";

import { isMarketOpenForBets } from "@/utilities/markets";
import { isOutright } from "@/utilities/sharedBettingUtilities";
import { isRacingEvent } from "@/features/betslip/builders/types";
import type {
  ExoticSubSelectionInput,
  ExoticToggleInput,
  MatchToggleInput,
  OutrightToggleInput,
  QuaddieToggleInput,
  RaceToggleInput,
  SemSubSelectionInput,
  SemToggleInput,
  ToggleSelectionInput,
} from "@/features/betslip/builders/types";
import type { QuaddieSelections, QuaddieType } from "@/features/betslip/types";
import type { MysteryMulti } from "@/features/sem-generator/util/constants";
import {
  isBettingEvent,
  type ProcessedEvent,
} from "@/hooks/firestore/betting/useEvent";
import type { QuaddieMarket } from "@/hooks/firestore/betting/useQuaddieMarkets";
import type {
  ProcessedExoticMultiSelection,
  ProcessedSameEventMultiSelection,
  ProcessedSelection,
  ProcessedSingleSelection,
} from "@/hooks/firestore/betting/useEntries";
import { getSelectionMarketStatus } from "@/features/entries/util/selection-helpers";
import {
  isExoticSelection,
  isQuaddieSelection,
  isSameEventMultiSelection,
  isSingleSelection,
} from "@/features/entries/util/type-guards";

const findShareMarket = (event: ProcessedEvent, marketId: string) => {
  if (isBettingEvent(event) && event.mainMarket?.id === marketId) {
    return event.mainMarket;
  }
  return event.markets.find((candidate) => candidate.id === marketId);
};

export const buildShareSingleInput = (
  event: ProcessedEvent,
  selection: ProcessedSingleSelection,
): MatchToggleInput | OutrightToggleInput | RaceToggleInput | undefined => {
  const market = findShareMarket(event, selection.marketId);
  const outcome = market?.outcomes?.[selection.outcomeId];
  if (!market || !outcome) return undefined;

  if (isRacingEvent(event)) {
    return {
      kind: "race",
      event,
      market,
      outcome,
      outcomeId: selection.outcomeId,
      hub: "racing",
      markets: event.markets,
      competitors: Object.values(event.competitors ?? {}),
      originType: "BETSHARE",
    };
  }

  const hub = event.hub === "esports" ? "esports" : "sports";

  if (isOutright(event.eventType ?? "")) {
    return {
      kind: "outright",
      event,
      market,
      outcome,
      outcomeId: selection.outcomeId,
      hub,
      originType: "BETSHARE",
    };
  }

  return {
    kind: "match",
    event,
    market,
    outcome,
    outcomeId: selection.outcomeId,
    hub,
    originType: "BETSHARE",
  };
};

export const buildShareExoticInput = (
  event: ProcessedEvent,
  selection: ProcessedExoticMultiSelection,
): ExoticToggleInput | undefined => {
  if (!isRacingEvent(event)) return undefined;

  const openOutcomes = Object.entries(selection.outcomes ?? {}).filter(
    ([, outcome]) => isMarketOpenForBets(outcome.marketStatus),
  );
  if (openOutcomes.length <= 1) return undefined;

  const market = findShareMarket(event, openOutcomes[0][1].marketId);
  if (!market) return undefined;

  const subSelections: ExoticSubSelectionInput[] = openOutcomes.map(
    ([outcomeId, outcome]) => ({
      outcomeId,
      marketId: outcome.marketId,
      name: outcome.outcomeName,
      marketName: outcome.marketName,
      outcomeType: outcome.outcomeType,
      attributes: outcome.attributes ?? undefined,
      competitor: outcome.competitor?.id
        ? event.competitors?.[outcome.competitor.id]
        : undefined,
    }),
  );

  return {
    kind: "exotic",
    event,
    market,
    hub: "racing",
    outcomeId: uuid.v4(),
    subSelections,
    markets: event.markets,
    competitors: Object.values(event.competitors ?? {}),
    originType: "BETSHARE",
  };
};

// Entries don't store outcomeGroup, so it's read off the live outcome.
const buildShareSemSubSelections = (
  outcomes: ProcessedSameEventMultiSelection["outcomes"] | undefined,
  event: ProcessedEvent,
): SemSubSelectionInput[] =>
  Object.entries(outcomes ?? {}).flatMap(([outcomeId, outcome]) => {
    if (!isMarketOpenForBets(outcome.marketStatus)) return [];
    const liveOutcome = findShareMarket(event, outcome.marketId)?.outcomes?.[
      outcomeId
    ];
    return [
      {
        outcomeId,
        marketId: outcome.marketId,
        name: outcome.outcomeName,
        marketName: outcome.marketName,
        outcomeType: outcome.outcomeType,
        marketType: outcome.marketType,
        competitor: outcome.competitor?.id
          ? event.competitors?.[outcome.competitor.id]
          : undefined,
        competitorId: outcome.competitor?.id,
        outcomeGroup: liveOutcome?.outcomeGroup,
      },
    ];
  });

export const buildShareSemInput = (
  event: ProcessedEvent,
  selection: ProcessedSameEventMultiSelection,
  mysteryMulti?: MysteryMulti,
): SemToggleInput | undefined => {
  const subSelections = buildShareSemSubSelections(selection.outcomes, event);
  if (subSelections.length <= 1) return undefined;

  if (isRacingEvent(event)) {
    return {
      kind: "sem",
      event,
      hub: "racing",
      outcomeId: uuid.v4(),
      odds: selection.odds,
      subSelections,
      markets: event.markets,
      competitors: Object.values(event.competitors ?? {}),
      originType: "BETSHARE",
      originId: selection.originId,
      mysteryMulti,
    };
  }

  if (event.eventType !== "MATCH") return undefined;

  return {
    kind: "sem",
    event,
    hub: "sports",
    outcomeId: uuid.v4(),
    odds: selection.odds,
    subSelections,
    markets: event.markets,
    competitors: Object.values(event.competitors ?? {}),
    originType: "BETSHARE",
    originId: selection.originId,
    mysteryMulti,
  };
};

export const buildShareQuaddieInput = (
  event: ProcessedEvent,
  market: QuaddieMarket,
  selections: ProcessedSelection[],
  quaddieType?: QuaddieType,
): QuaddieToggleInput | undefined => {
  if (!isRacingEvent(event)) return undefined;

  const quaddieSelections: QuaddieSelections = {};
  for (const selection of selections) {
    if (!isQuaddieSelection(selection)) continue;

    const raceNumber = selection.event.attributes?.raceNumber;
    quaddieSelections[selection.event.eventId] = Object.keys(
      selection.outcomes,
    ).map((outcomeId) => ({
      outcomeId,
      raceNumber: Number(raceNumber || 0),
      runnerNumber: Number(
        selection.outcomes?.[outcomeId]?.attributes?.runnerNumber || 0,
      ),
    }));
  }

  return {
    kind: "quaddie",
    outcomeId: uuid.v4(),
    event,
    market,
    hub: "racing",
    quaddieSelections,
    quaddieType: quaddieType ?? market.type,
    markets: event.markets,
    competitors: Object.values(event.competitors ?? {}),
    originType: "BETSHARE",
  };
};

/**
 * Routes a shared entry selection to its kind-specific input builder.
 * Returns undefined when the selection can't (or shouldn't) be re-bet:
 * closed market, unresolvable market/outcome, or a quaddie — those go
 * through ShareQuaddie with their own market document instead.
 */
export const buildSharePickInput = (
  event: ProcessedEvent,
  selection: ProcessedSelection,
  mysteryMulti?: MysteryMulti,
): ToggleSelectionInput | undefined => {
  if (isSingleSelection(selection)) {
    if (!isMarketOpenForBets(getSelectionMarketStatus(selection))) {
      return undefined;
    }
    return buildShareSingleInput(event, selection);
  }

  if (isExoticSelection(selection)) {
    if (!isMarketOpenForBets(getSelectionMarketStatus(selection))) {
      return undefined;
    }
    return buildShareExoticInput(event, selection);
  }

  if (isSameEventMultiSelection(selection)) {
    return buildShareSemInput(event, selection, mysteryMulti);
  }

  return undefined;
};
